import {
  ComparedField,
  ImportReviewReason,
  type RankedCandidate,
  RowPatchConflictCode,
  type RowPatchConflictCodeValue,
  type SessionRow,
} from '@/api/contact-import/types';

/**
 * The existing contact that already owns one of this row's emails, or null when none does. Such a row
 * cannot be imported as a new contact — the server rejects a `CreateNew` patch for it with
 * `EmailAlreadyExists`, so the resolver disables that option rather than offering one that can only
 * fail. Candidates arrive ranked, so the first email match is the best one.
 */
export const emailMatchedCandidate = (row: SessionRow | undefined): RankedCandidate | null =>
  row?.candidates?.find((candidate) => candidate.matchedFields.includes(ComparedField.Email)) ??
  null;

export const EMAIL_MATCH_BLOCKED_TOAST = {
  title: 'A contact with this email already exists',
  description:
    'This row can only be merged into the existing contact, or skipped. Pick the matching contact above.',
} as const;

const NAME_MISSING_HINT =
  'This contact has no name. A full name is required to add it as a new contact.';
const NAME_CONFIRM_HINT =
  'This contact has no full name, which is required. Confirm it before adding as new.';

export const hasUsableName = (value: string | null | undefined): boolean =>
  /\p{L}/u.test(value ?? '');

export const fullNameRequiredHint = (row: SessionRow | undefined): string =>
  hasUsableName(row?.payload?.fullName) ? NAME_CONFIRM_HINT : NAME_MISSING_HINT;

const keepAsNewBlocker = (row: SessionRow): 'emailTaken' | 'needsName' | null => {
  if (row.decision != null) return null;
  if (emailMatchedCandidate(row)) return 'emailTaken';
  if (
    row.reviewReason === ImportReviewReason.FullNameRequired &&
    !hasUsableName(row.payload?.fullName)
  ) {
    return 'needsName';
  }
  return null;
};

export const keepAsNewLeftoverToast = (
  rows: readonly SessionRow[],
): { title: string; description: string } | null => {
  const emailTaken = rows.filter((row) => keepAsNewBlocker(row) === 'emailTaken').length;
  const needsName = rows.filter((row) => keepAsNewBlocker(row) === 'needsName').length;
  const total = emailTaken + needsName;
  if (total === 0) return null;

  const description =
    emailTaken && needsName
      ? 'Some have an email that already belongs to an existing contact, and some still need a full name.'
      : emailTaken
        ? 'Their email already belongs to an existing contact, so they cannot be added as new.'
        : 'They still need a full name before they can be added as new.';

  const subject = total === 1 ? '1 row still needs' : `${total} rows still need`;
  return { title: `${subject} review`, description };
};

/** A patch returned in `conflicts` was NOT applied — keep the user on the row instead of advancing. */
export const rowPatchConflictMessage = (code: RowPatchConflictCodeValue): string => {
  switch (code) {
    case RowPatchConflictCode.EmailAlreadyExists:
      return EMAIL_MATCH_BLOCKED_TOAST.description;
    case RowPatchConflictCode.FullNameRequired:
      return 'Add a full name before this contact can be added as new.';
    case RowPatchConflictCode.FullNameNotEditable:
      return 'This row does not take a full name — only rows flagged as needing one can be named here.';
    case RowPatchConflictCode.NotFound:
      return 'This row is no longer part of the import. Reopen the import to see the current preview.';
    case RowPatchConflictCode.InvalidTarget:
      return 'That contact can no longer be merged into. Pick a different match, or add this row as new.';
    default:
      return 'This row changed elsewhere while you were reviewing it, so your choice was not saved. Check the refreshed details and choose again.';
  }
};

// Keyed by field AND value: the same text can legitimately appear under two fields (a job title that
// matches a company name), and only the field it is actually present on may be hidden. Values are
// compared case/whitespace-insensitively because the server emits them raw.
const fieldValueKey = (field: string, value: string): string =>
  `${field}\u0000${value.trim().toLowerCase()}`;

/** Empty for no match, or for a candidate predating `alreadyPresentFields` — then nothing is hidden. */
export const alreadyPresentValueKeys = (candidate: RankedCandidate | null): ReadonlySet<string> =>
  new Set(
    (candidate?.alreadyPresentFields ?? []).map((entry) => fieldValueKey(entry.field, entry.value)),
  );

export const isValueHidden = (hidden: ReadonlySet<string>, field: string, value: string): boolean =>
  hidden.has(fieldValueKey(field, value));
